import {
  activeRoutineFilterCount,
  NO_ROUTINE_FILTERS,
  type FilterOption,
  type RoutineFilterState,
} from '../../../lib/routine-filters';
import { useTranslation } from '../../../lib/i18n';
import { ClearFiltersButton } from '../category-view/CategoryView';
import { agentMark, CategoryFilterMenu, useSpaceMark } from '../category-view/CategoryFilterMenu';
import styles from './RoutinesFilterBar.module.css';

// The Routines list's filter row: **Space, then Agent**, the same pair and the same shared menu
// (`CategoryFilterMenu`) the Tasks row draws, then one "Clear filters · N".
//
// The options are **passed in, not derived here**: the host clamps its state against the same lists
// (`clampRoutineFilters`), and a list derived twice is a list that can disagree with itself. A menu is drawn
// as soon as it has one option, so every category's row reads "All spaces ⌄ · All agents ⌄".
export function RoutinesFilterBar({
  spaces,
  agents = [],
  filters,
  onChange,
  onBi,
  className,
}: {
  /** The Spaces worth offering — `spacesOf(routines, workspaces)`. Empty when the host fixes this axis. */
  spaces: FilterOption[];
  /** The agents worth offering — `agentsOf(routines, team)`. Empty when the host fixes this axis. */
  agents?: FilterOption[];
  filters: RoutineFilterState;
  onChange: (next: RoutineFilterState) => void;
  /** Reports which filter was used, for BI. */
  onBi?: (name: string) => void;
  /** Layout from the enclosing row — the bar owns its chips, not its place on the page. */
  className?: string;
}) {
  const { t } = useTranslation();
  const spaceMark = useSpaceMark();

  const pick = (name: string, next: RoutineFilterState) => {
    onBi?.(name);
    onChange(next);
  };

  return (
    <div className={className ? `${styles.bar} ${className}` : styles.bar}>
      {spaces.length > 0 && (
        <CategoryFilterMenu
          options={spaces.map((space) => ({ id: space.id, name: space.name, icon: spaceMark(space.id, space.name) }))}
          selectedId={filters.workspaceId}
          allLabel={t('routines.filter.allSpaces')}
          ariaLabel={t('packs.section.workspace')}
          onPick={(id) => pick(id ? 'filter_space' : 'filter_space_all', { ...filters, workspaceId: id })}
          onOpen={() => onBi?.('filter_space_open')}
        />
      )}
      {agents.length > 0 && (
        <CategoryFilterMenu
          options={agents.map((agent) => ({ id: agent.id, name: agent.name, icon: agentMark(agent.id) }))}
          selectedId={filters.agentId}
          allLabel={t('tasks.filter.allAgents')}
          ariaLabel={t('island.agent')}
          onPick={(id) => pick(id ? 'filter_agent' : 'filter_agent_all', { ...filters, agentId: id })}
          onOpen={() => onBi?.('filter_agent_open')}
        />
      )}
      <ClearFiltersButton count={activeRoutineFilterCount(filters)} onClear={() => pick('filter_clear', NO_ROUTINE_FILTERS)} />
    </div>
  );
}
